import React from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { desktop, mobile } from '../../styles/media'
import Icon from '../../components/general/Icon'
import currencies from '../../data/currencies'
import {
	CARD_MARKET,
	priceSourceCurrencies,
	priceSourceNames
} from '../../data/priceSources'
import Tag from '../../components/general/Tag'
import DropDown, {
	DropDownLink,
	DropDownLinkCheck,
	DropDownLinkRight,
	HeaderLinkDropDownHeading
} from '../../components/button/DropDown'
import { updateUserSetting } from '../../state/actionCreators/app'
import history from '../../helper/history'
import Link from '../../components/router/Link'
import translate from '../../helper/translate'
import { compose } from 'redux'
import { priceSourceOptions } from '../../data'
import { priceSourceDefaults, translationsMapping } from '../../../config'
import { events, trackEvent } from '../../helper/track'

const PriceSourceDropDown = ({ authToken, settings, subscribed, updateUserSetting, userLanguage, t }) => {
	const priceSource = Number(settings['app.priceSource']) || CARD_MARKET

	const options = priceSourceOptions(priceSourceDefaults[translationsMapping[userLanguage]])
	const firstPriceSource = options[0]
	const secondPriceSource = options[1]

	const handleChange = (newPriceSource) => {
		updateUserSetting('app.priceSource', newPriceSource, true)
		trackEvent(events.SITE_CHANGE_PRICE_SOURCE, {
			priceSource: `[${priceSourceCurrencies[newPriceSource]}] ${priceSourceNames[newPriceSource]}`
		})
	}

	const button = (
		<StyledPriceSourceDropDown onClick={!authToken ? () => history.push('/signup') : null}>
			<Tag>{currencies[priceSourceCurrencies[priceSource]].name}</Tag>
			{priceSourceNames[priceSource]}
		</StyledPriceSourceDropDown>
	)

	return (
		<Wrapper>
			{authToken ? (
				<DropDown
					button={button}
					top={32}
				>
					{onBeforeNavigate => (
						<>
							<HeaderLinkDropDownHeading>
								{firstPriceSource.label} <Tag>{firstPriceSource.currency}</Tag>
							</HeaderLinkDropDownHeading>
							{firstPriceSource.options.map(item => {
								const active = priceSource === Number(item.value)

								return (
									<DropDownLink
										onClick={() => handleChange(item.value)}
										onBeforeNavigate={onBeforeNavigate}
										active={active}
										disabled={!subscribed && item.value !== firstPriceSource.value}
										key={item.value}
									>
										{item.label}
										<DropDownLinkRight>
											<DropDownLinkCheck active={active}>
												<Icon name="check" />
											</DropDownLinkCheck>
										</DropDownLinkRight>
									</DropDownLink>
								)
							})}

							{!subscribed && (
								<UpgradeHint>
									{t('general.upgradeToProToAccessPriceData', {
										upgradeToPro: (
											<Link to="/pricing" onBeforeNavigate={onBeforeNavigate}>
												{t('general.upgradeToProToAccessPriceData2')}
											</Link>
										)
									})}
								</UpgradeHint>
							)}

							<HeaderLinkDropDownHeading>
								{secondPriceSource.label} <Tag>{secondPriceSource.currency}</Tag>
							</HeaderLinkDropDownHeading>
							{secondPriceSource.options.map(item => {
								const active = priceSource === Number(item.value)

								return (
									<DropDownLink
										onClick={() => handleChange(item.value)}
										onBeforeNavigate={onBeforeNavigate}
										active={active}
										disabled={!subscribed && item.value !== secondPriceSource.value}
										key={item.value}
									>
										{item.label}
										<DropDownLinkRight>
											<DropDownLinkCheck active={active}>
												<Icon name="check" />
											</DropDownLinkCheck>
										</DropDownLinkRight>
									</DropDownLink>
								)
							})}
						</>
					)}
				</DropDown>
			) : button}
		</Wrapper>
	)
}

PriceSourceDropDown.propTypes = {
	authToken: PropTypes.string,
	settings: PropTypes.object.isRequired,
	subscribed: PropTypes.string,
	updateUserSetting: PropTypes.func.isRequired,
	userLanguage: PropTypes.string.isRequired,
	t: PropTypes.func.isRequired
}

PriceSourceDropDown.defaultProps = {
	authToken: null,
	subscribed: null
}

const Wrapper = styled.div`
	${Tag} {
		background: ${props => props.theme.secondary};
		border-radius: 2px;
		color: ${props => props.theme.white};
		display: inline;
		font-size: 0.7rem;
	}
`

const StyledPriceSourceDropDown = styled.button`
	background: transparent;
	border: 0;
	color: ${props => props.theme.text};
	cursor: pointer;
	padding: 0;
	transition: 0.2s;

	align-items: center;
	display: flex;
	justify-content: center;
	gap: 0.25rem;

	color: ${props => props.theme.textLight};
	font-size: 0.8rem;
	font-weight: 500;

	height: 30px;
	min-width: 30px;

	svg {
		height: 16px;
		width: 16px;
	}

	${desktop`
		&:hover {
			opacity: 0.5;
		}
	`}
`

const UpgradeHint = styled.div`
	background: ${props => props.theme.backgroundVeryLight};
	color: ${props => props.theme.textLight};
	font-size: 0.8rem;
	line-height: 1.3;
	margin: 0.5rem 0;
	padding: 0.5rem 0.75rem;

	a {
		color: ${props => props.theme.id === 'dark' ? props.theme.yellow : props.theme.primary};
		font-weight: 600;
		text-decoration: underline;
	}

	${mobile`
		padding: 0.75rem 1.5rem;
	`}
`

const enhance = compose(
	connect(state => ({
		authToken: state.persist.authToken,
		settings: state.persist.user.settings,
		subscribed: state.persist.user.subscribed,
		userLanguage: state.persist.language
	}), dispatch => bindActionCreators({
		updateUserSetting
	}, dispatch)),
	translate('general')
)

export default enhance(PriceSourceDropDown)
